import React, { memo } from 'react';
import PropTypes from 'prop-types';

/**
 * Professional Wave Divider Component
 * 
 * A highly configurable wave divider that seamlessly separates sections
 * with animated wave patterns. Optimized for performance and accessibility.
 * 
 * @param {Object} props - Component props
 * @param {'top'|'bottom'} props.position - Position of the wave divider
 * @param {'white'|'blue'|'purple'|'gradient'} props.color - Color theme
 * @param {'smooth'|'dramatic'|'gentle'|'sharp'} props.variant - Wave style variant
 * @param {boolean} props.animated - Enable wave animation
 * @param {number} props.height - Height of the wave in pixels
 * @param {string} props.className - Additional CSS classes
 * @param {Object} props.style - Additional inline styles
 */
const WaveDivider = memo(({
  position = 'bottom',
  color = 'white',
  variant = 'smooth',
  animated = true,
  height = 120,
  className = '',
  style = {},
  ...props
}) => {
  
  // Wave path definitions for different variants - now with proper scaling and full coverage
  const waveVariants = {
    smooth: `M0,${height * 0.2}L48,${height * 0.25}C96,${height * 0.3},192,${height * 0.4},288,${height * 0.4}C384,${height * 0.4},480,${height * 0.3},576,${height * 0.27}C672,${height * 0.23},768,${height * 0.27},864,${height * 0.3}C960,${height * 0.33},1056,${height * 0.37},1152,${height * 0.35}C1248,${height * 0.33},1344,${height * 0.27},1392,${height * 0.23}L1440,${height * 0.2}L1440,${height}L1392,${height}C1344,${height},1248,${height},1152,${height}C1056,${height},960,${height},864,${height}C768,${height},672,${height},576,${height}C480,${height},384,${height},288,${height}C192,${height},96,${height},48,${height}L0,${height}Z`,
    
    dramatic: `M0,${height * 0.5}L48,${height * 0.43}C96,${height * 0.37},192,${height * 0.23},288,${height * 0.25}C384,${height * 0.27},480,${height * 0.43},576,${height * 0.48}C672,${height * 0.53},768,${height * 0.47},864,${height * 0.43}C960,${height * 0.4},1056,${height * 0.4},1152,${height * 0.43}C1248,${height * 0.47},1344,${height * 0.53},1392,${height * 0.57}L1440,${height * 0.6}L1440,${height}L1392,${height}C1344,${height},1248,${height},1152,${height}C1056,${height},960,${height},864,${height}C768,${height},672,${height},576,${height}C480,${height},384,${height},288,${height}C192,${height},96,${height},48,${height}L0,${height}Z`,
    
    gentle: `M0,${height * 0.3}L48,${height * 0.35}C96,${height * 0.4},192,${height * 0.5},288,${height * 0.5}C384,${height * 0.5},480,${height * 0.4},576,${height * 0.38}C672,${height * 0.37},768,${height * 0.43},864,${height * 0.45}C960,${height * 0.47},1056,${height * 0.43},1152,${height * 0.42}C1248,${height * 0.4},1344,${height * 0.4},1392,${height * 0.4}L1440,${height * 0.4}L1440,${height}L1392,${height}C1344,${height},1248,${height},1152,${height}C1056,${height},960,${height},864,${height}C768,${height},672,${height},576,${height}C480,${height},384,${height},288,${height}C192,${height},96,${height},48,${height}L0,${height}Z`,
    
    sharp: `M0,${height * 0.2}L48,${height * 0.28}C96,${height * 0.37},192,${height * 0.53},288,${height * 0.57}C384,${height * 0.6},480,${height * 0.5},576,${height * 0.47}C672,${height * 0.43},768,${height * 0.47},864,${height * 0.43}C960,${height * 0.4},1056,${height * 0.3},1152,${height * 0.28}C1248,${height * 0.27},1344,${height * 0.33},1392,${height * 0.37}L1440,${height * 0.4}L1440,${height}L1392,${height}C1344,${height},1248,${height},1152,${height}C1056,${height},960,${height},864,${height}C768,${height},672,${height},576,${height}C480,${height},384,${height},288,${height}C192,${height},96,${height},48,${height}L0,${height}Z`
  };

  // Color configurations
  const colorMap = {
    white: '#ffffff',
    blue: '#f8fafc',
    purple: '#f3f4f6',
    gradient: 'url(#waveGradient)'
  };

  // Animation styles
  const animationStyle = animated ? {
    animation: 'waveFlow 12s ease-in-out infinite'
  } : {};

  // Position-based transformations
  const positionTransform = position === 'top' ? 'rotate(180deg)' : 'none';

  // Responsive scaling
  const viewBox = `0 0 1440 ${height}`;
  const scaledPath = waveVariants[variant] || waveVariants.smooth;

  return (
    <div 
      className={`wave-divider wave-divider--${position} wave-divider--${color} ${className}`}
      style={{
        position: 'absolute',
        [position]: 'var(--wave-divider-position)',
        left: '-50px',
        width: 'calc(100% + 100px)',
        height: `${height}px`,
        overflow: 'hidden',
        lineHeight: 0,
        transform: positionTransform,
        zIndex: 10,
        pointerEvents: 'none',
        ...animationStyle,
        ...style
      }}
      aria-hidden="true"
      {...props}
    >
      <svg
        viewBox={viewBox}
        preserveAspectRatio="none"
        style={{
          position: 'relative',
          display: 'block',
          width: 'calc(100% + 100px)',
          height: '100%',
          marginLeft: '-50px',
          marginRight: '-50px'
        }}
      >
        {/* Gradient definition for gradient color option */}
        {color === 'gradient' && (
          <defs>
            <linearGradient id="waveGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#667eea" />
              <stop offset="50%" stopColor="#764ba2" />
              <stop offset="100%" stopColor="#667eea" />
            </linearGradient>
          </defs>
        )}
        
        {/* Solid background to prevent bleeding */}
        <rect
          x="0"
          y={height * 0.5}
          width="1440"
          height={height * 0.5}
          fill={colorMap[color] || colorMap.white}
        />
        
        {/* Wave path */}
        <path
          d={scaledPath}
          fill={colorMap[color] || colorMap.white}
          style={{
            transition: 'all 0.3s ease'
          }}
        />
      </svg>
    </div>
  );
});

WaveDivider.displayName = 'WaveDivider';

WaveDivider.propTypes = {
  position: PropTypes.oneOf(['top', 'bottom']),
  color: PropTypes.oneOf(['white', 'blue', 'purple', 'gradient']),
  variant: PropTypes.oneOf(['smooth', 'dramatic', 'gentle', 'sharp']),
  animated: PropTypes.bool,
  height: PropTypes.number,
  className: PropTypes.string,
  style: PropTypes.object
};

export default WaveDivider; 